import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { saveUserEntry } from '../utils/db';
import { 
  playBubblingSound, 
  playCorrectChime, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiRotateCcw, FiUser, FiArrowRight, FiSmile } = FiIcons;

const PRIMARY_TUBES = [
  { id: 'red', name: 'Ruby Red', color: 'bg-red-500', hex: '#ef4444', emoji: '🧪' },
  { id: 'blue', name: 'Ocean Blue', color: 'bg-blue-500', hex: '#3b82f6', emoji: '🧪' },
  { id: 'yellow', name: 'Sun Yellow', color: 'bg-yellow-400', hex: '#facc15', emoji: '🧪' }
];

const POTION_RECIPES = {
  'red+blue': { name: 'Magical Violet Purple', hex: '#a855f7', emoji: '🔮', desc: 'Red and Blue blend into royal purple!' },
  'blue+red': { name: 'Magical Violet Purple', hex: '#a855f7', emoji: '🔮', desc: 'Red and Blue blend into royal purple!' },
  'blue+yellow': { name: 'Glowing Forest Green', hex: '#10b981', emoji: '🧪', desc: 'Blue and Yellow make leafy emerald green!' },
  'yellow+blue': { name: 'Glowing Forest Green', hex: '#10b981', emoji: '🧪', desc: 'Blue and Yellow make leafy emerald green!' },
  'red+yellow': { name: 'Fiery Sunset Orange', hex: '#f97316', emoji: '✨', desc: 'Red and Yellow create bright warm orange!' },
  'yellow+red': { name: 'Fiery Sunset Orange', hex: '#f97316', emoji: '✨', desc: 'Red and Yellow create bright warm orange!' }
};

const ColorLabFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState('welcome'); // 'welcome' | 'lab' | 'won'
  const [kidName, setKidName] = useState('');
  const [beakerColors, setBeakerColors] = useState([]);
  const [discoveredPotions, setDiscoveredPotions] = useState([]);
  const [currentPotion, setCurrentPotion] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [couponCode, setCouponCode] = useState('');

  const handleStartLab = (e) => {
    e.preventDefault();
    setStep('lab');
    playTickSound(700, 0.05);
  };

  const handlePourTube = (tubeId) => {
    playBubblingSound();

    if (beakerColors.includes(tubeId)) return;
    const newBeaker = [...beakerColors, tubeId];
    setBeakerColors(newBeaker);

    if (newBeaker.length === 2) {
      const key = `${newBeaker[0]}+${newBeaker[1]}`;
      const result = POTION_RECIPES[key];

      if (result) {
        setCurrentPotion(result);
        playCorrectChime();

        if (!discoveredPotions.some(p => p.name === result.name)) {
          const nextDiscovered = [...discoveredPotions, result];
          setDiscoveredPotions(nextDiscovered);

          // If child discovered 3 primary combinations!
          if (nextDiscovered.length >= 3) {
            setTimeout(() => {
              const code = 'CHEM-' + Math.random().toString(36).substring(2, 7).toUpperCase();
              setCouponCode(code);

              saveUserEntry({
                name: kidName.trim() || 'Junior Chemist',
                mobile: 'Ages 3-7 Wonder Lab',
                classGrade: 'KG / Primary STEM',
                reward: '🧪 Junior Wonder Chemist Award Badge',
                couponCode: code,
                gameType: 'Magic Color Lab'
              });

              setStep('won');
              playWinFanfare();
            }, 1000);
          }
        }
      }
    }
  };

  const handleClearBeaker = () => {
    setBeakerColors([]);
    setCurrentPotion(null);
    playBubblingSound();
  };

  const handleResetGame = () => {
    setKidName('');
    setBeakerColors([]);
    setDiscoveredPotions([]);
    setCurrentPotion(null);
    setStep('welcome');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 select-none ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 text-slate-900'
    }`}>
      {step === 'won' && <ConfettiEffect />}

      {/* Header */}
      <header className="flex items-center justify-between pb-3 max-w-4xl mx-auto w-full border-b border-purple-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all ${
            isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-purple-200 text-slate-700 shadow-sm'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🧪</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-purple-600 via-pink-600 to-amber-500">
                Little Alchemist: Magic Color Lab
              </h1>
            </div>
            <p className="text-[11px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wider">
              Ages 3-7 STEM Chemistry • Mix Primary Colors to Make Magic Potions!
            </p>
          </div>
        </div>

        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
            isDarkMode ? 'bg-amber-400 text-slate-950 border-amber-300 shadow-md' : 'bg-white text-slate-800 border-purple-200 shadow-sm'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
          <span>{isDarkMode ? 'Day ☀️' : 'Night 🌙'}</span>
        </button>
      </header>

      {/* Main Flow Area */}
      <main className="flex-1 flex flex-col items-center justify-center my-6 max-w-2xl mx-auto w-full">
        <AnimatePresence mode="wait">
          
          {/* Welcome Screen */}
          {step === 'welcome' && (
            <motion.div
              key="welcome"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border text-center shadow-xl ${
                isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-purple-200 shadow-purple-200/40'
              }`}
            >
              <div className="flex justify-center gap-2 text-5xl mb-4 animate-bounce">
                <span>🧪</span>
                <span>🎨</span>
                <span>✨</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mb-2">
                Welcome Little Scientist!
              </h2>

              <p className="text-sm text-slate-500 mb-6">
                Discover what happens when you pour colorful liquids together in our magic beaker!
              </p>

              <form onSubmit={handleStartLab} className="space-y-4">
                <div className="text-left">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <SafeIcon icon={FiUser} className="text-purple-500" />
                    <span>Little Chemist's Name (Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={kidName}
                    onChange={e => setKidName(e.target.value)}
                    placeholder="e.g. Diya / Aryan"
                    className="w-full px-4 py-3.5 rounded-2xl border text-base font-bold focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-amber-400 text-white font-black text-lg shadow-xl shadow-purple-500/30 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                >
                  <span>Open Magic Color Lab! 🧪</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </form>
            </motion.div>
          )}

          {/* Interactive Lab Screen */}
          {step === 'lab' && (
            <motion.div
              key="lab"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full flex flex-col items-center space-y-6"
            >
              {/* Progress: Potions Discovered */}
              <div className="flex items-center gap-3 px-5 py-2 rounded-full bg-white dark:bg-slate-900 border border-purple-200 dark:border-slate-700 shadow-sm">
                <span className="text-xs font-black uppercase text-purple-600 dark:text-purple-400">
                  {kidName || 'Little Chemist'}'s Magic Discoveries:
                </span>
                <div className="flex gap-1.5">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <span 
                      key={i} 
                      className={`text-lg transition-transform ${
                        i < discoveredPotions.length ? 'text-amber-400 scale-125' : 'text-slate-300 dark:text-slate-700'
                      }`}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
                <span className="text-xs font-bold text-slate-400">
                  {discoveredPotions.length}/3 Potions
                </span>
              </div>

              {/* Mixing Beaker Display */}
              <div className="relative w-64 h-64 flex flex-col items-center justify-end p-4">
                {/* Flask Glass Outline */}
                <div className="w-48 h-52 rounded-b-3xl rounded-t-lg border-4 border-slate-300 dark:border-slate-600 bg-white/40 dark:bg-slate-900/40 relative overflow-hidden flex flex-col justify-end p-2 shadow-2xl backdrop-blur-sm">
                  
                  {/* Liquid inside beaker */}
                  <motion.div
                    className="w-full rounded-b-2xl transition-all duration-700 relative"
                    style={{
                      height: beakerColors.length === 0 ? '10%' : beakerColors.length === 1 ? '45%' : '80%',
                      backgroundColor: currentPotion ? currentPotion.hex : beakerColors.length === 1 ? PRIMARY_TUBES.find(t => t.id === beakerColors[0])?.hex : '#e2e8f0'
                    }}
                  >
                    {/* Bubbling bubbles animation inside liquid */}
                    {beakerColors.length > 0 && (
                      <motion.div
                        animate={{ y: [-5, 5, -5] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="text-2xl text-center pt-2 opacity-80"
                      >
                        🫧 🫧
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Measurement tick marks */}
                  <div className="absolute top-10 right-2 w-3 h-0.5 bg-slate-400"></div>
                  <div className="absolute top-20 right-2 w-4 h-0.5 bg-slate-400"></div>
                  <div className="absolute top-30 right-2 w-3 h-0.5 bg-slate-400"></div>
                </div>
              </div>

              {/* Potion Result Text */}
              <div className="min-h-[60px] text-center">
                {currentPotion ? (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="p-3 rounded-2xl bg-white dark:bg-slate-900 border-2 border-purple-400 shadow-md inline-block"
                  >
                    <span className="text-sm font-black text-slate-900 dark:text-white block">
                      {currentPotion.emoji} {currentPotion.name}
                    </span>
                    <span className="text-xs text-slate-500">{currentPotion.desc}</span>
                  </motion.div>
                ) : beakerColors.length === 1 ? (
                  <p className="text-xs font-bold text-slate-500">
                    Now tap a second test tube to see the color blend!
                  </p>
                ) : (
                  <p className="text-xs font-bold text-slate-400">
                    Tap any two test tubes below to pour and mix!
                  </p>
                )}
              </div>

              {/* Test Tubes To Tap */}
              <div className="flex gap-4">
                {PRIMARY_TUBES.map(tube => (
                  <button
                    key={tube.id}
                    onClick={() => handlePourTube(tube.id)}
                    className="flex flex-col items-center p-3 rounded-2xl bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-700 shadow-md hover:scale-110 active:scale-90 transition-transform cursor-pointer"
                  >
                    <div className={`w-8 h-16 rounded-full ${tube.color} shadow-inner flex items-end justify-center pb-2 text-white font-black text-xs`}>
                      {tube.id === 'red' ? 'R' : tube.id === 'blue' ? 'B' : 'Y'}
                    </div>
                    <span className="text-xs font-black mt-2 text-slate-800 dark:text-white">{tube.name}</span>
                  </button>
                ))}
              </div>

              {/* Reset Beaker Button */}
              {beakerColors.length > 0 && (
                <button
                  onClick={handleClearBeaker}
                  className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-300 dark:border-slate-700 text-slate-500 hover:text-slate-800 dark:hover:text-white transition-colors cursor-pointer"
                >
                  Empty Beaker for Next Mix 🧪
                </button>
              )}
            </motion.div>
          )}

          {/* Winning Screen */}
          {step === 'won' && (
            <motion.div
              key="won"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border text-center shadow-2xl ${
                isDarkMode ? 'bg-slate-900 border-purple-500/40' : 'bg-white border-purple-200 shadow-purple-200/50'
              }`}
            >
              <div className="w-20 h-20 rounded-full bg-gradient-to-tr from-purple-500 via-pink-400 to-amber-400 text-white flex items-center justify-center text-4xl mx-auto mb-3 shadow-xl">
                <SafeIcon icon={FiAward} />
              </div>

              <div className="text-xs font-black uppercase text-purple-600 tracking-widest mb-1">
                🌟 Super Chemist Award!
              </div>

              <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2">
                Hooray, {kidName || 'Little Chemist'}!
              </h2>

              <p className="text-xs text-slate-500 mb-6">
                You mastered color mixing and brewed 3 magical potions!
              </p>

              <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-950/40 border border-purple-300 dark:border-purple-700/50 mb-6">
                <span className="text-xs text-purple-700 dark:text-purple-300 font-black uppercase tracking-wider block mb-1">
                  🎁 STEM Science Pass
                </span>
                <span className="text-lg font-black text-slate-900 dark:text-white">
                  Junior Wonder Chemist Badge
                </span>
              </div>

              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl inline-block mb-6 border border-slate-300 dark:border-slate-700">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                  Gift Voucher Code
                </p>
                <p className="text-lg font-mono font-black text-purple-600 dark:text-purple-400">
                  {couponCode}
                </p>
              </div>

              <button
                onClick={handleResetGame}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-600 text-white font-black text-base shadow-lg shadow-purple-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Brew Again! 🧪</span>
              </button>
            </motion.div>
          )}

        </AnimatePresence>
      </main>

      <footer className="text-center text-xs text-slate-400 py-2 border-t border-purple-100 dark:border-slate-800">
        GamifyCenter Junior STEM Lab • Primary Color Theory & Liquids
      </footer>
    </div>
  );
};

export default ColorLabFlow;
